import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonText } from '../../components/Skeleton';
import type { Appointment } from './api';
import { addDays, rangeOf, today, touchesDay } from './dates';
import { formatTimes } from './presentation';
import { useAppointments } from './queries';

/**
 * "Heutige Termine" und "Termine fuer morgen" auf der Startseite -- F1-53.
 *
 * **Eine Abfrage fuer beide Tage**, aufgeteilt in der Oberflaeche nach
 * derselben Regel wie beim Server: Ueberschneidung, nicht Beginn. Ein
 * Nachteinsatz von 23 bis 1 Uhr steht damit in beiden Spalten -- und das ist
 * richtig, denn er betrifft beide Tage.
 *
 * **Nur die eigenen.** Die Termine der Kollegen sind Einsatzplanung, und die
 * baut dieses System nicht (docs/06, "Bewusst nicht gebaut").
 *
 * Keine abwaehlbare Kachel: Wie "Meine Arbeit" sagt sie, was der Betrachter
 * selbst zu tun hat. Wer keine Termine hat, sieht zwei kurze Leerzustaende.
 */
export function UpcomingAppointmentsCard() {
  const { t } = useTranslation();
  const heute = today();
  const morgen = addDays(heute, 1);
  const query = useAppointments({ mine: true, ...rangeOf(heute, morgen) });

  const list = query.data ?? [];

  return (
    <section className="card" aria-label={t('appointments.title')} aria-busy={query.isPending}>
      <div className="chart-card__head">
        <div className="page-header__title">
          <h2>{t('appointments.title')}</h2>
          <Link to="/app/calendar" className="nav__item">
            {t('appointments.toCalendar')}
          </Link>
        </div>
        <p className="chart-card__hint">{t('appointments.dashboardHint')}</p>
      </div>

      {query.isPending ? <SkeletonText lines={3} /> : null}
      {query.isError ? <ErrorNotice error={query.error} /> : null}

      {query.isSuccess ? (
        <div className="appointment-days">
          <DayColumn
            title={t('appointments.today')}
            empty={t('appointments.todayEmpty')}
            items={list.filter((item) => touchesDay(item.starts_at, item.ends_at, heute))}
          />
          <DayColumn
            title={t('appointments.tomorrow')}
            empty={t('appointments.tomorrowEmpty')}
            items={list.filter((item) => touchesDay(item.starts_at, item.ends_at, morgen))}
          />
        </div>
      ) : null}
    </section>
  );
}

function DayColumn({
  title,
  empty,
  items,
}: {
  title: string;
  empty: string;
  items: Appointment[];
}) {
  const { i18n } = useTranslation();

  return (
    <div>
      <h3>{title}</h3>
      {items.length === 0 ? (
        <EmptyState icon="calendar" title={empty} />
      ) : (
        <ul className="my-work">
          {items.map((item) => (
            <li key={item.id} className="my-work__row">
              <span className="stack" style={{ gap: 'var(--space-1)', minWidth: 0 }}>
                <span className="table__number">
                  {formatTimes(item.starts_at, item.ends_at, i18n.language)}
                </span>
                <Link
                  to="/app/tickets/$ticketId"
                  params={{ ticketId: item.ticket_id }}
                  className="my-work__title"
                >
                  {item.ticket_number} · {item.ticket_title}
                </Link>
                {item.customer_name || item.location ? (
                  <span className="table__muted">
                    {[item.customer_name, item.location].filter(Boolean).join(' · ')}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
